import {GridValidRowModel} from "@mui/x-data-grid/models/gridRows";
import {GridRenderCellParams} from "@mui/x-data-grid";
import {OverflowTip} from "../OverflowTip";
import React from "react";
import {OverflowChip} from "../OverflowChip";
import {CopyButton} from "../CopyButton";
import {Checkbox} from "@mui/material";

export function textColumn<V = any, R extends GridValidRowModel = any, F = V>(
	converter: (text: string) => string = (it) => it,
): (params: GridRenderCellParams<R, V, F>) => JSX.Element {
	return (params: GridRenderCellParams<R, V, F>) => {
		const value = params.formattedValue?.toString();
		if (value)
			return <span>{converter(value)}</span>;
		return <span>{value}</span>;
	};
}

export function uuidColumn<R extends GridValidRowModel = any, V = any, F = V>(): (params: GridRenderCellParams<R, V, F>) => JSX.Element {
	return (params: GridRenderCellParams<R, V, F>) => {
		return <span style={{
			fontFamily: "monospace"
		}}>{params.formattedValue?.toString()}</span>;
	};
}

export function checkboxColumn<R extends GridValidRowModel = any, V = any>(): (params: GridRenderCellParams<R, V, boolean>) => JSX.Element {
	return (params: GridRenderCellParams<R, V, boolean>) => {
		return <Checkbox
			checked={params.formattedValue}
			disabled={!params.isEditable}
		/>;
	};
}

export function tooltipColumn<V = any, R extends GridValidRowModel = any, F = V>(
	child: (params: GridRenderCellParams<R, V, F>) => JSX.Element
): (params: GridRenderCellParams<R, V, F>) => JSX.Element {
	return (params: GridRenderCellParams<R, V, F>) => {
		return <OverflowTip title={params.formattedValue?.toString()}>
			{child(params)}
		</OverflowTip>;
	};
}

export function copyableColumn<V = any, R extends GridValidRowModel = any, F = V>(
	child: (params: GridRenderCellParams<R, V, F>) => JSX.Element
): (params: GridRenderCellParams<R, V, F>) => JSX.Element {
	return (params: GridRenderCellParams<R, V, F>) => {
		const text = params.formattedValue?.toString();
		if (text)
			return <>
				<CopyButton getText={() => text}/>
				{child(params)}
			</>;
		else
			return child(params);
	};
}

/**
 * input values: V1 | V2 | V3
 */
export function adnResColumn<V = any, R extends GridValidRowModel = any, F = V>(
	child: (params: GridRenderCellParams<R, V, F>) => JSX.Element
): (params: GridRenderCellParams<R, V, F>) => JSX.Element {
	return (params: GridRenderCellParams<R, V, F>) => {
		const value = params.value?.toString();
		
		let color: "success" | "warning" | "error" | "info";
		if (!value)
			return <span></span>;
		else if (value === "V1") // Accepted
			color = "success";
		else if (value === "V2") // Rejected
			color = "error";
		else if (value === "V3") // Under Review
			color = "info";
		else
			color = "warning"; // unknown
		
		return <OverflowChip label={child(params)} color={color} variant="outlined"/>;
	};
}

/**
 * input values: QUEUED | PROCESSING | QUEUED_FOR_SAVING | ERROR | UNDER_REVIEW | DONE
 */
export function orderStatusColumn<V = any, R extends GridValidRowModel = any, F = V>(): (params: GridRenderCellParams<R, V, F>) => JSX.Element {
	return (params: GridRenderCellParams<R, V, F>) => {
		const value = params.value?.toString();
		
		let color: "success" | "warning" | "error" | "info";
		if (!value)
			return <span></span>;
		else if (value === "QUEUED")
			color = "info";
		else if (value === "PROCESSING")
			color = "info";
		else if (value === "QUEUED_FOR_SAVING")
			color = "info";
		else if (value === "ERROR")
			color = "error";
		else if (value === "UNDER_REVIEW")
			color = "info";
		else if (value === "DONE")
			color = "success";
		else
			color = "warning";
		
		return <OverflowChip label={params.formattedValue?.toString()} color={color} variant="outlined"/>;
	};
}

/**
 * input values: ACTIVE | DELETED | HIDDEN | INACTIVE
 */
export function productSupplierStatusColumn<V = any, R extends GridValidRowModel = any, F = V>(): (params: GridRenderCellParams<R, V, F>) => JSX.Element {
	return (params: GridRenderCellParams<R, V, F>) => {
		const value = params.value?.toString();
		
		let color: "success" | "warning" | "error" | "info";
		if (!value)
			return <span></span>;
		else if (value === "ACTIVE")
			color = "success";
		else if (value === "DELETED")
			color = "error";
		else if (value === "HIDDEN")
			color = "info";
		else if (value === "INACTIVE")
			color = "warning";
		else
			color = "warning";
		
		return <OverflowChip label={params.formattedValue?.toString()} color={color} variant="outlined"/>;
	};
}

/**
 * input values: HELLO_FRESH_COUPON
 */
export function productIncentiveTypeColumn<V = any, R extends GridValidRowModel = any, F = V>(): (params: GridRenderCellParams<R, V, F>) => JSX.Element {
	return (params: GridRenderCellParams<R, V, F>) => {
		const value = params.value?.toString();
		if (!value)
			return <span></span>;
		
		return <OverflowChip label={params.formattedValue?.toString()} color={"success"} variant="outlined"/>;
	};
}

/**
 * input values: ACTIVE | DELETED
 */
export function productStatusColumn<V = any, R extends GridValidRowModel = any, F = V>(): (params: GridRenderCellParams<R, V, F>) => JSX.Element {
	return (params: GridRenderCellParams<R, V, F>) => {
		const value = params.value?.toString();
		
		let color: "success" | "warning" | "error";
		if (!value)
			return <span></span>;
		else if (value === "ACTIVE")
			color = "success";
		else if (value === "DELETED")
			color = "error";
		else
			color = "warning";
		
		return <OverflowChip label={params.formattedValue?.toString()} color={color} variant="outlined"/>;
	};
}

/**
 * input values: ACTIVE | DELETED
 */
export function entityStatusColumn<V = any, R extends GridValidRowModel = any, F = V>(): (params: GridRenderCellParams<R, V, F>) => JSX.Element {
	return (params: GridRenderCellParams<R, V, F>) => {
		const value = params.value?.toString();
		
		let color: "success" | "warning" | "error";
		if (!value)
			return <span></span>;
		else if (value === "ACTIVE")
			color = "success";
		else if (value === "DELETED")
			color = "error";
		else
			color = "warning";
		
		return <OverflowChip label={params.formattedValue?.toString()} color={color} variant="outlined"/>;
	};
}

/**
 * input values: AFFILIATE | FUNNEL_MGA | NOT_ORDERABLE
 */
export function productOrderTypeColumn<V = any, R extends GridValidRowModel = any, F = V>(): (params: GridRenderCellParams<R, V, F>) => JSX.Element {
	return (params: GridRenderCellParams<R, V, F>) => {
		const value = params.value?.toString();
		
		let color: "success" | "info" | "warning" | "error";
		if (!value)
			return <span></span>;
		else if (value === "AFFILIATE")
			color = "info";
		else if (value === "FUNNEL_MGA")
			color = "success";
		else if (value === "NOT_ORDERABLE")
			color = "error";
		else
			color = "warning";
		
		return <OverflowChip label={params.formattedValue?.toString()} color={color} variant="outlined"/>;
	};
}

/**
 * input values: NON_SPECIFIC | CAR | ENERGY | HOME | LIABILITY | TRIPLE_PLAY | TRAVEL | HEALTH_CARE | FUNERAL | LOAN | SIM_ONLY | ANIMAL | BIKE | SAVINGS
 */
export function productCategoryColumn<V = any, R extends GridValidRowModel = any, F = V>(): (params: GridRenderCellParams<R, V, F>) => JSX.Element {
	return (params: GridRenderCellParams<R, V, F>) => {
		return <OverflowChip label={params.formattedValue?.toString()} color={"info"} variant="outlined"/>;
	};
}
